import { IDLE_ACTIVITY_STORAGE_KEYS } from '@supply-mover-2-0/core';
import type {
	PortalClientDto,
	PortalProfileDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTenant } from '../components/TenantGate';
import { signOutPortal } from '../lib/portalSignOut';
import {
	refreshAccessToken,
	setupCrossTabSync,
} from '../lib/portalTokenService';
import type { PortalLoginResponse } from '../services/portalAuthService';
import * as portalAuthService from '../services/portalAuthService';
import * as portalCartService from '../services/portalCartService';
import { useCartStore } from '../stores/cartStore';
import {
	restorePersistedSession,
	usePortalAuthStore,
} from '../stores/portalAuthStore';

/**
 * PD-16414 — a new sign-in starts the idle clock from zero. Sign-out already
 * removes the record (portalAuthStore.reset); this covers a record some
 * other path left behind, which would otherwise read as hours of idleness
 * and sign the shopper straight back out.
 */
function startFreshIdleClock(): void {
	try {
		localStorage.removeItem(IDLE_ACTIVITY_STORAGE_KEYS.storefront);
	} catch {
		// Storage unavailable: nothing to clear.
	}
}

/**
 * Fetch the current cart and update the cart store.
 * Called after login completes and on session restore so the
 * header badge and AddToCartControl states are accurate immediately.
 */
async function loadCart(token: string): Promise<void> {
	try {
		const response = await portalCartService.getCart(token);
		if (response.success && response.data != null) {
			useCartStore.getState().setCart(response.data);
		}
	} catch {
		// Cart fetch failed — non-critical, will retry when cart page opens
	}
}

interface UsePortalAuthReturn {
	token: string | null;
	profile: PortalProfileDto | null;
	/** Derived active client object (resolved from activeClientId + availableClients) */
	activeClient: PortalClientDto | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	availableClients: Array<PortalClientDto>;
	activeClientId: number | null;
	needsClientSelection: boolean;
	login: (email: string, password: string) => Promise<void>;
	/**
	 * PD-16471 — send a sign-in code (and link) to an email address or phone
	 * number. The channel it went out on comes back with the expiry.
	 */
	requestSignInCode: (identifier: string) => Promise<{
		expiresIn: number;
		channel: portalAuthService.SignInCodeChannel;
	}>;
	/** PD-16471 — sign in with the code from that message. */
	loginWithCode: (identifier: string, code: string) => Promise<void>;
	/** PD-16471 — sign in with the link from that message (or a welcome text). */
	loginWithLink: (token: string) => Promise<void>;
	/**
	 * Biometric quick sign-in: exchange a keychain-stored refresh token
	 * for a session. False = token stale/revoked; fall back to credentials.
	 */
	loginWithRefreshToken: (refreshToken: string) => Promise<boolean>;
	selectClient: (clientId: number) => Promise<void>;
	logout: () => void;
	refreshSession: () => Promise<void>;
}

export function usePortalAuth(): UsePortalAuthReturn {
	const store = usePortalAuthStore();
	const { alias: tenantAlias } = useTenant();
	const { refreshTranslations } = useTranslation();
	const hasRestoredSession = useRef(false);

	// On mount: set up cross-tab sync and attempt session restore
	useEffect(() => {
		// Listen for auth changes from other tabs (token refresh, logout)
		const cleanupSync = setupCrossTabSync();

		// Attempt to restore a previous session from localStorage.
		// The store was seeded with isRestoringSession=true at module load
		// when a persisted token was present (see portalAuthStore.ts), so
		// route guards know to wait. We clear that flag here on every
		// terminal path — success, failure, or "no token to restore" —
		// so the guards can finally branch on the resolved auth state.
		if (!hasRestoredSession.current) {
			hasRestoredSession.current = true;

			const stored = restorePersistedSession();
			if (stored?.token != null && stored.refreshToken != null) {
				const state = usePortalAuthStore.getState();

				// Only restore if we're not already authenticated (avoids overwriting
				// state that was set by a login that just completed in this tab)
				if (!state.isAuthenticated && state.token == null) {
					state.setToken(stored.token);
					state.setRefreshToken(stored.refreshToken);
					state.setExpiresAt(stored.expiresAt);
					if (stored.languagePreference != null) {
						state.setLanguagePreference(stored.languagePreference);
					}
					state.setLoading(true);

					// Validate the stored token by fetching the profile.
					// If the token is expired, the apiClient's 401 retry will
					// attempt a refresh automatically.
					portalAuthService
						.me(stored.token)
						.then(async (meResult) => {
							// The server handles auto-selection for single-client users
							// in /auth/me, so activeClientId will only be null here if
							// the user has multiple clients and hasn't picked one yet.
							const s = usePortalAuthStore.getState();
							s.setProfile(meResult.profile);
							s.setAvailableClients(meResult.clients);
							s.setActiveClientId(meResult.activeClientId);
							// Before setAuthenticated: the idle guard starts on
							// the flip and checks this restored session's last
							// activity against the limit straight away.
							s.setIdleTimeoutMinutes(meResult.idleTimeoutMinutes);
							s.setAuthenticated(true);
							s.setNeedsClientSelection(
								meResult.clients.length > 1 && meResult.activeClientId == null,
							);
							s.setDisplaySettings(meResult.displaySettings);

							// Update language from authoritative /me response
							if (meResult.languagePreference != null) {
								s.setLanguagePreference(meResult.languagePreference);
								s.persist();
							}

							s.setLoading(false);
							s.setRestoringSession(false);

							// Load translations after session restore
							await refreshTranslations();

							// Load cart if we have an active client
							const currentToken = usePortalAuthStore.getState().token;
							if (currentToken != null && meResult.activeClientId != null) {
								await loadCart(currentToken);
							}
						})
						.catch((error: unknown) => {
							// PD-14684: only a real auth REJECTION may end the
							// session. A navigation away / reload ABORTS this
							// in-flight /auth/me (net::ERR_ABORTED → TypeError)
							// — and this catch used to run reset() in the dying
							// document, wiping the persisted session out of
							// localStorage right as the next page load went
							// looking for it. That was the silent
							// return-to-Welcome during rapid navigation. The
							// same applies to any transient network failure:
							// the session is not invalid just because we
							// couldn't ask.
							const status = (error as { status?: unknown }).status;
							if (status === 401 || status === 403) {
								// Server explicitly rejected the token AND the
								// apiClient's refresh retry — clear everything.
								// reset() sets state back to initialState which
								// has isRestoringSession=false, so guards
								// unblock and fall through to the
								// unauthenticated redirect.
								usePortalAuthStore.getState().reset();
								return;
							}
							// Aborted or transient failure — keep the persisted
							// session for the next load and just unblock the
							// route guards on this one.
							const s = usePortalAuthStore.getState();
							s.setLoading(false);
							s.setRestoringSession(false);
						});
				} else {
					// Another usePortalAuth instance from the same render pass
					// already adopted the persisted token and kicked off
					// /auth/me. Do NOT clear restoringSession here — that
					// instance is still waiting on the network round-trip.
					// Its .then() (or .catch via reset()) is the only thing
					// that knows when the gate can safely drop.
					//
					// The earlier version cleared the gate eagerly, assuming
					// "parallel instance has already finished". With ~30
					// usePortalAuth call sites mounting from the same
					// PortalLayout subtree on the very first render, the
					// second instance fires a microtask AFTER the first has
					// set the token but LONG before /auth/me lands. Clearing
					// here let PortalLayout drop its splash with
					// isAuthenticated still false, HomePage observed the
					// bootstrap's isAnonymousVisitor=true, and bounced
					// authenticated visitors to /catalog instead of
					// /dashboard on every refresh of `/`.
				}
			} else {
				// No persisted session to restore. The module-load peek may
				// have seen a token that was cleared between then and now
				// (e.g. devtools, another tab's logout) — clear the flag so
				// guards don't hang on a restore that will never happen.
				usePortalAuthStore.getState().setRestoringSession(false);
			}
		}

		return cleanupSync;
	}, []);

	/**
	 * Shared post-login flow: store tokens, fetch profile, handle
	 * client selection, and load cart. Used by both password login
	 * and SMS login so the post-auth behavior is identical.
	 */
	const handleLoginSuccess = useCallback(
		async (loginResult: PortalLoginResponse) => {
			const state = usePortalAuthStore.getState();
			const expiresAt = Date.now() + loginResult.expiresIn * 1000;

			state.setToken(loginResult.accessToken);
			state.setRefreshToken(loginResult.refreshToken);
			state.setExpiresAt(expiresAt);

			// Seed language preference from login response, then persist tokens + language
			state.setLanguagePreference(loginResult.languagePreference ?? 'en');
			state.persist();

			// Fetch profile + client list from crm-api.
			// The server auto-selects for single-client users in /auth/me,
			// so activeClientId will only be null if multi-client with no selection.
			const meResult = await portalAuthService.me(loginResult.accessToken);
			state.setProfile(meResult.profile);
			state.setAvailableClients(meResult.clients);
			state.setActiveClientId(meResult.activeClientId);
			startFreshIdleClock();
			state.setIdleTimeoutMinutes(meResult.idleTimeoutMinutes);
			state.setAuthenticated(true);
			state.setNeedsClientSelection(
				meResult.clients.length > 1 && meResult.activeClientId == null,
			);
			state.setDisplaySettings(meResult.displaySettings);

			// Update language preference from /me response (authoritative source)
			if (meResult.languagePreference != null) {
				state.setLanguagePreference(meResult.languagePreference);
				state.persist();
			}

			// Load translations now that we have auth + language preference
			await refreshTranslations();

			// Load cart if we have an active client
			if (meResult.activeClientId != null) {
				await loadCart(loginResult.accessToken);
			}
		},
		[refreshTranslations],
	);

	const login = useCallback(
		async (email: string, password: string) => {
			const state = usePortalAuthStore.getState();
			state.setLoading(true);
			try {
				// Brand/company is derived server-side from the request
				// hostname; the frontend doesn't pass anything brand-related.
				const loginResult = await portalAuthService.login(
					email,
					password,
					tenantAlias,
				);

				// Password max-age (PD-14144): the portal password expired. Send
				// the user into the login server's existing reset flow (with an
				// "expired" banner), returning to this storefront afterward.
				if (
					loginResult.requiresPasswordChange === true &&
					loginResult.resetToken != null
				) {
					const authBase = import.meta.env.VITE_AUTH_SERVER_URL;
					const resetUrl =
						`${authBase}/auth/reset-password` +
						`?token=${encodeURIComponent(loginResult.resetToken)}` +
						`&expired=true&redirect=${encodeURIComponent(window.location.origin)}`;
					window.location.href = resetUrl;
					return;
				}

				await handleLoginSuccess(loginResult);
			} catch (error: unknown) {
				// PD-14684 rule, applied to login (found via PD-13773): only a
				// real auth rejection may end the session. A reload/navigation
				// right after a successful login aborts the trailing post-login
				// fetches (profile/translations/cart) — running reset() in the
				// dying document wiped the just-persisted session out of
				// localStorage, so the next page load came up signed out.
				const status = (error as { status?: unknown }).status;
				if (status === 401 || status === 403) {
					usePortalAuthStore.getState().reset();
				}
				throw error;
			} finally {
				usePortalAuthStore.getState().setLoading(false);
			}
		},
		[tenantAlias, handleLoginSuccess],
	);

	/**
	 * Request an SMS verification code for the given phone number.
	 * Returns the code expiry time so the UI can display a countdown.
	 */
	const requestSignInCode = useCallback(
		async (
			identifier: string,
		): Promise<{
			expiresIn: number;
			channel: portalAuthService.SignInCodeChannel;
		}> => {
			const result = await portalAuthService.requestSignInCode(
				identifier,
				tenantAlias,
			);
			return { expiresIn: result.expiresIn, channel: result.channel };
		},
		[tenantAlias],
	);

	/**
	 * Verify a sign-in code and complete authentication. The post-login flow
	 * (profile, client selection, cart) is identical to password sign-in.
	 */
	const loginWithCode = useCallback(
		async (identifier: string, code: string) => {
			const state = usePortalAuthStore.getState();
			state.setLoading(true);
			try {
				const loginResult = await portalAuthService.verifySignInCode(
					identifier,
					code,
					tenantAlias,
				);
				await handleLoginSuccess(loginResult);
			} finally {
				// Don't reset the auth store on verification failure — the user
				// was never authenticated, so there's nothing to clear. Resetting
				// here triggers a store update that cascades to other components
				// (e.g. UserMenu) during render, causing a React warning.
				usePortalAuthStore.getState().setLoading(false);
			}
		},
		[tenantAlias, handleLoginSuccess],
	);

	const loginWithLink = useCallback(
		async (token: string) => {
			const state = usePortalAuthStore.getState();
			state.setLoading(true);
			try {
				const loginResult = await portalAuthService.verifySignInLink(
					token,
					tenantAlias,
				);
				await handleLoginSuccess(loginResult);
			} finally {
				usePortalAuthStore.getState().setLoading(false);
			}
		},
		[tenantAlias, handleLoginSuccess],
	);

	const selectClient = useCallback(async (clientId: number) => {
		const state = usePortalAuthStore.getState();
		const currentToken = state.token;
		if (currentToken == null) {
			throw new Error('Not authenticated');
		}

		state.setLoading(true);

		// Clear old client's cart immediately so stale data doesn't flash
		useCartStore.getState().setCart(null);

		try {
			const result = await portalAuthService.selectClient(
				currentToken,
				clientId,
			);
			state.setActiveClientId(result.activeClientId);
			state.setNeedsClientSelection(false);
			state.setAuthenticated(true);

			// PD-14239: the effective display flags (canSeePricing /
			// canViewInvoices / canPayInvoices) are driven by the per-user-
			// PER-CLIENT hidePricing flag, so they change with the active
			// client. Re-fetch /auth/me after switching so the storefront
			// reflects the newly-selected client's flags instead of the
			// previous client's stale settings.
			const meResult = await portalAuthService.me(currentToken);
			state.setDisplaySettings(meResult.displaySettings);
			state.setIdleTimeoutMinutes(meResult.idleTimeoutMinutes);

			await loadCart(currentToken);
		} finally {
			usePortalAuthStore.getState().setLoading(false);
		}
	}, []);

	// PD-16414 — also ends the session on the server (web), not just here.
	// signOutPortal clears local state before its first await, so callers
	// can navigate and open the sign-in dialog straight after.
	const logout = useCallback(() => {
		void signOutPortal({ revoke: true });
	}, []);

	/**
	 * Biometric quick sign-in (PD-14601): exchange a keychain-stored
	 * refresh token for a fresh session. Mirrors handleLoginSuccess but
	 * sources tokens from the refresh endpoint instead of a credential
	 * login. Returns false when the stored token is stale or revoked —
	 * the caller should clear the stored biometric session and fall back
	 * to credential login.
	 */
	const loginWithRefreshToken = useCallback(
		async (refreshToken: string): Promise<boolean> => {
			const state = usePortalAuthStore.getState();
			state.setLoading(true);
			try {
				state.setRefreshToken(refreshToken);
				const accessToken = await refreshAccessToken();
				if (accessToken == null) {
					return false;
				}
				const meResult = await portalAuthService.me(accessToken);
				const s = usePortalAuthStore.getState();
				s.setProfile(meResult.profile);
				s.setAvailableClients(meResult.clients);
				s.setActiveClientId(meResult.activeClientId);
				startFreshIdleClock();
				s.setIdleTimeoutMinutes(meResult.idleTimeoutMinutes);
				s.setAuthenticated(true);
				s.setNeedsClientSelection(
					meResult.clients.length > 1 && meResult.activeClientId == null,
				);
				s.setDisplaySettings(meResult.displaySettings);
				if (meResult.languagePreference != null) {
					s.setLanguagePreference(meResult.languagePreference);
				}
				s.persist();
				await refreshTranslations();
				if (meResult.activeClientId != null) {
					await loadCart(accessToken);
				}
				return true;
			} catch {
				usePortalAuthStore.getState().reset();
				return false;
			} finally {
				usePortalAuthStore.getState().setLoading(false);
			}
		},
		[refreshTranslations],
	);

	const refreshSession = useCallback(async () => {
		const state = usePortalAuthStore.getState();
		const currentToken = state.token;
		if (currentToken == null) {
			return;
		}

		try {
			const result = await portalAuthService.me(currentToken);
			state.setProfile(result.profile);
			state.setAvailableClients(result.clients);
			state.setActiveClientId(result.activeClientId);
			state.setDisplaySettings(result.displaySettings);
			state.setIdleTimeoutMinutes(result.idleTimeoutMinutes);
			state.setAuthenticated(true);
		} catch (error: unknown) {
			// PD-14684: same rule as the boot restore — only a real auth
			// rejection ends the session; an aborted/transient request must
			// not wipe a healthy login.
			const status = (error as { status?: unknown }).status;
			if (status === 401 || status === 403) {
				state.reset();
			}
		}
	}, []);

	// Derive the full active client object from the ID + available clients list
	const activeClient = useMemo(
		() =>
			store.activeClientId != null
				? (store.availableClients.find((c) => c.id === store.activeClientId) ??
					null)
				: null,
		[store.activeClientId, store.availableClients],
	);

	return {
		token: store.token,
		profile: store.profile,
		activeClient,
		isAuthenticated: store.isAuthenticated,
		isLoading: store.isLoading,
		availableClients: store.availableClients,
		activeClientId: store.activeClientId,
		needsClientSelection: store.needsClientSelection,
		login,
		requestSignInCode,
		loginWithCode,
		loginWithLink,
		loginWithRefreshToken,
		selectClient,
		logout,
		refreshSession,
	};
}
